import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";

import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { Subscription as SubscriptionData } from "data/models";
import { scheduleCancellation } from "data/mutations-common";
import { formatTimestamp } from "utils";
import { resolveIdentityId } from "utils/identity";

interface CancelSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscription: SubscriptionData;
}

export const CancelSubscriptionDialog = ({
  open,
  onOpenChange,
  subscription,
}: CancelSubscriptionDialogProps) => {
  const [identity] = useIdentity();
  const identityId = resolveIdentityId(identity);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const active = subscription.activeSubscription!;

  const scheduleCancellationMutation = useMutation({
    mutationFn: () => scheduleCancellation(),
    onSuccess: () => {
      queryClient.setQueryData([QueryKeys.subscription, identityId], {
        ...subscription,
        activeSubscription: {
          ...subscription.activeSubscription,
          cancelAtPeriodEnd: true,
        },
      });
      onOpenChange(false);
      enqueueSnackbar(
        "You successfully scheduled a subscription cancellation.",
        { variant: "success" },
      );
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirm Cancellation</DialogTitle>
          <DialogDescription asChild>
            <div>
              You're about to cancel your{" "}
              <strong>{active.plan.display_name}</strong> subscription. Your
              access will continue until the end of your current billing term on{" "}
              <strong>
                {formatTimestamp(active.renewalDate * 1000)}
              </strong>
              . After this date, your cancellation will be complete, and all
              members who are not the owner will lose access to the Hub at the
              end of the current term.
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Go Back
          </Button>
          <Button
            variant="destructive"
            disabled={scheduleCancellationMutation.isPending}
            onClick={() => scheduleCancellationMutation.mutate()}
          >
            {scheduleCancellationMutation.isPending ? (
              <>
                <Spinner />
                Cancelling...
              </>
            ) : (
              "Cancel Subscription"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
